import { useEffect, useState } from 'react';
import type { NeglectedRegion } from '@ambit/shared';
import { api } from '../api.ts';

/** Below this, a region has been visited recently enough not to nag about. */
const NAG_AFTER_DAYS = 45;

/**
 * The counterweight to a stable, ranked hero list: without something pointing
 * at the parts of the map that have gone quiet, a catalog of 176 things
 * collapses into the same dozen favourites. This is the one place the visit log
 * earns its keep in the planning flow rather than just being a diary.
 */
export function NeglectedRegions({
  activeRegion,
  onPick,
  onClear,
}: {
  activeRegion: string | null;
  onPick: (region: NeglectedRegion) => void;
  onClear: () => void;
}) {
  const [regions, setRegions] = useState<NeglectedRegion[]>([]);

  useEffect(() => {
    void api
      .listNeglectedRegions(3)
      .then(setRegions)
      .catch(() => undefined);
  }, []);

  if (activeRegion) {
    const picked = regions.find((r) => r.regionId === activeRegion);
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-spruce-800 px-3 py-2">
        <span className="text-sm text-bone-300">
          Just {picked?.regionName ?? activeRegion}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-xs text-bone-500 underline decoration-dotted underline-offset-4 transition-colors hover:text-blaze-300"
        >
          show everywhere
        </button>
      </div>
    );
  }

  const worth = regions.filter((r) => r.visitCount === 0 || (r.daysSinceVisit ?? 0) >= NAG_AFTER_DAYS);
  if (worth.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-sm text-bone-500">
        Somewhere you haven&rsquo;t been in a while:
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {worth.map((region) => (
          <button
            key={region.regionId}
            type="button"
            onClick={() => onPick(region)}
            className="rounded-md bg-spruce-800 px-3 py-1.5 text-left text-sm text-bone-300 transition-colors hover:bg-spruce-700 hover:text-bone-100"
          >
            {region.regionName}
            <span className="numeric ml-2 text-xs text-bone-500">
              {region.visitCount === 0 ? 'never' : `${region.daysSinceVisit}d`}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
